import { createRouter, createWebHistory } from 'vue-router';
import VueRouterMetaTags from '@bachdgvn/vue-router-meta-tags';
import routes from './routes';
import { setAuthState } from '../composables/auth';

import { handOffToInertia, redirectToLogin } from './helpers';
import { hasServerSession } from '../utils/helpers';
import { handleChunkError } from '../errors/chunk-error';
import { clearChunkReloadFlag, reportChunkReloadExhausted } from '../bootstrap/chunk-reload';
import { trackSpaPageViews } from '../bootstrap/analytics';
import { useJobBoardStore } from '../store/job-board.store.js';
import { useJobAlertsStore } from '../store/job-alerts.store.js';
import { exitIntentGuard } from './exit-intent-guard';

const { isAuthenticated, logout, isOnboardingComplete, getUser } = setAuthState();

const router = createRouter({ history: createWebHistory(), routes });

// vue-router calls this synchronously, then rejects the navigation with the same error, which also
// reaches app.js's unhandledrejection listener when the caller did not catch it. Running first, this
// handler decides, and the shared latch in handleChunkError makes the listener stand down - so the
// reload lands on the page the user asked for rather than the one they were leaving.
router.onError((error, to) => {
	handleChunkError(error, () => to?.fullPath || window.location.pathname, reportChunkReloadExhausted);
});

// check if route requires auth
const canUserAccess = (to) => {
	if (to.matched.some((record) => record.meta.requiresAuth)) {
		// A present server session (data-user-id, rendered by the Inertia login handoff) counts
		// as authenticated even before localStorage is hydrated, so the freshly-authenticated
		// user is not bounced to /login while /api/user hydrates the SPA.
		return isAuthenticated() || hasServerSession();
	}
	return true;
};

const IGNORE_ONBOARDING_REDIRECT_AFTER_REGISTER_ON_ROUTES = [
	'resumes.edit',
	'community',
	'job-board-search',
	'cover-letters.index',
	'interview-ai',
	'interview-ai-create',
	'networking-tool',
	'checkout'
];

// Must stay first: a page Inertia serves is left with a full page load before any SPA guard,
// meta tag update or redirect acts on a route the SPA is not going to render.
router.beforeEach(handOffToInertia);

router.beforeEach(VueRouterMetaTags.update);

router.afterEach(() => {
	clearChunkReloadFlag();
});

router.beforeEach(async (to, from) => {
	const urlParams = from.query;

	if (!isOnboardingComplete() && to.name !== 'onboarding' && to.name !== '404' && isAuthenticated() && IGNORE_ONBOARDING_REDIRECT_AFTER_REGISTER_ON_ROUTES.indexOf(to.name) === -1) {
		return { path: '/onboarding', query: urlParams };
	}

	if (to.path === '/logout') {
		await logout();

		useJobBoardStore().resetState();
		useJobAlertsStore().resetState();

		return redirectToLogin(urlParams);
	}

	if (!canUserAccess(to)) {
		return redirectToLogin(urlParams);
	}

	if (to.meta.feature && isAuthenticated()) {
		const featureKey = to.meta.feature.replace(/_/g, '-');
		const userFeatures = getUser()?.features;
		if (userFeatures && featureKey in userFeatures && !userFeatures[featureKey]) {
			return { name: 'home' };
		}
	}
});

router.beforeEach(exitIntentGuard);

trackSpaPageViews(router, getUser);

export default router;
